import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { AuthUser, fetchAuthConfig, requestPasswordReset } from '../api/auth';
import MfaEnrollment from '../components/MfaEnrollment';
import BrandLockup from './BrandLockup';
import { useAuth } from '../context/AuthContext';
import { appTheme, pitchTheme } from '../styles/theme';
import { Button } from './ui/Button';
import PasswordInput from './ui/PasswordInput';
import {
  AuthCodeInput,
  AuthField,
  AuthFieldError,
  AuthFieldLabel,
  AuthInputIcon,
  AuthInputShell,
  AuthTextInput,
} from './ui/authFieldStyles';
import {
  CredentialsField,
  validateCredentials,
  validateEmail,
  validateLoginPassword,
  validateMfaCode,
  validateName,
  validateRegisterPassword,
} from '../utils/authValidation';

const Overlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 250;
  background: rgba(10, 46, 40, 0.4);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 0.25s ease;
`;

const Drawer = styled.aside<{ $open: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  z-index: 260;
  width: min(420px, 100vw);
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px;
  border-left: 1px solid ${appTheme.border};
  background: ${appTheme.surface};
  box-shadow: ${appTheme.shadowLg};
  transform: translateX(${({ $open }) => ($open ? '0' : '100%')});
  transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
  overflow-y: auto;
`;

const DrawerBrandRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 24px;
`;

const Intro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  margin: 0;
  font-family: ${pitchTheme.fontPageTitle};
  font-size: 1.375rem;
  font-weight: 500;
  color: ${appTheme.text};
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
  color: ${appTheme.textMuted};
`;

const CloseButton = styled.button`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border: 1px solid ${appTheme.border};
  border-radius: ${appTheme.radiusMd};
  background: ${appTheme.surface};
  color: ${appTheme.textMuted};
  font-size: 1.25rem;
  line-height: 1;
  cursor: pointer;

  &:hover {
    background: ${appTheme.surfaceHover};
    color: ${appTheme.text};
  }

  &:focus-visible {
    outline: 2px solid ${appTheme.green};
    outline-offset: 2px;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const FieldError = AuthFieldError;

const FullWidthButton = styled(Button)`
  width: 100%;
  margin-top: 4px;
`;

const ErrorMessage = styled.p`
  margin: 0;
  padding: 10px 12px;
  border-radius: ${appTheme.radiusMd};
  background: ${appTheme.dangerBg};
  border: 1px solid ${appTheme.dangerBorder};
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${appTheme.dangerText};
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 20px 0 16px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${appTheme.textMuted};

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${appTheme.border};
  }
`;

const GoogleButtonHost = styled.div<{ $visible: boolean }>`
  display: block;
  width: 100%;
  min-height: 44px;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  transition: opacity 0.2s ease;

  /* Let the Google-rendered button be the only bordered control. */
  & > div {
    width: 100% !important;
  }
`;

const SwitchModeText = styled.p`
  margin: 20px 0 0;
  text-align: center;
  font-size: 0.8125rem;
  color: ${appTheme.textMuted};
`;

const SwitchModeButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  font-family: inherit;
  font-size: inherit;
  font-weight: 700;
  color: ${appTheme.green};
  cursor: pointer;

  &:hover {
    color: ${appTheme.greenHover};
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${appTheme.green};
    outline-offset: 2px;
    border-radius: ${appTheme.radiusSm};
  }
`;

const ForgotPasswordRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 2px;
`;

const ForgotPasswordLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${appTheme.textMuted};
  cursor: pointer;

  &:hover {
    color: ${appTheme.green};
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${appTheme.green};
    outline-offset: 2px;
    border-radius: ${appTheme.radiusSm};
  }
`;

const GSI_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

type GoogleCredentialResponse = { credential: string };

const loadGsiScript = (): Promise<void> =>
  new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${GSI_SCRIPT_SRC}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = GSI_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('googleLoadError'));
    document.head.appendChild(script);
  });

/** Should this user see the (skippable) 2FA onboarding step after signing in? */
const shouldOfferMfaOnboarding = (user: AuthUser): boolean =>
  user.hasPassword && !user.totpEnabled && !user.mfaPromptDismissed;

type DrawerStep = 'credentials' | 'challenge' | 'onboarding' | 'forgot' | 'forgot-sent';

const LoginDrawer: React.FC = () => {
  const { t } = useTranslation();
  const formatError = (err: unknown): string => {
    if (err instanceof Error && err.message === 'googleLoadError') {
      return t('auth.googleLoadError');
    }
    return err instanceof Error ? err.message : t('auth.somethingWrong');
  };
  const {
    user,
    login,
    register,
    loginWithGoogle,
    verifyMfaLogin,
    applyUser,
    isLoginOpen,
    loginNext,
    closeLogin,
  } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [step, setStep] = useState<DrawerStep>('credentials');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<CredentialsField, string>>>({});
  const [mfaFieldError, setMfaFieldError] = useState<string | null>(null);
  const [forgotEmailError, setForgotEmailError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const googleHostRef = useRef<HTMLDivElement>(null);
  const [googleReady, setGoogleReady] = useState(false);
  const drawerRef = useRef<HTMLElement>(null);
  // True while a sign-in flow owns the drawer, so the auto-close effect below
  // doesn't dismiss the MFA steps the moment the user becomes signed in.
  const flowActiveRef = useRef(false);

  const finishAuth = () => {
    flowActiveRef.current = false;
    closeLogin();
    if (loginNext) {
      navigate(loginNext.startsWith('/') ? loginNext : `/${loginNext}`);
    }
  };

  /** After a session exists: either offer 2FA onboarding or close the drawer. */
  const completeAuth = (signedInUser: AuthUser) => {
    if (shouldOfferMfaOnboarding(signedInUser)) {
      setStep('onboarding');
      return;
    }
    finishAuth();
  };

  useEffect(() => {
    // Drawer opened while already signed in (and no flow in progress): nothing to do here.
    if (user && isLoginOpen && !flowActiveRef.current) {
      finishAuth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isLoginOpen]);

  // Reset transient state whenever the drawer closes.
  useEffect(() => {
    if (!isLoginOpen) {
      setStep('credentials');
      setMfaToken(null);
      setMfaCode('');
      setError(null);
      setFieldErrors({});
      setMfaFieldError(null);
      setForgotEmailError(null);
      setIsSubmitting(false);
      flowActiveRef.current = false;
    }
  }, [isLoginOpen]);

  useEffect(() => {
    if (!isLoginOpen) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeLogin();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [isLoginOpen, closeLogin]);

  useEffect(() => {
    if (!isLoginOpen || step !== 'credentials') {
      setGoogleReady(false);
      return;
    }

    let cancelled = false;

    const initGoogle = async () => {
      const { googleClientId } = await fetchAuthConfig();
      if (!googleClientId || cancelled) {
        return;
      }
      await loadGsiScript();
      if (cancelled || !googleHostRef.current) {
        return;
      }

      googleHostRef.current.innerHTML = '';

      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => resolve());
      });
      if (cancelled || !googleHostRef.current) {
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const google = (window as any).google;
      google.accounts.id.initialize({
        client_id: googleClientId,
        callback: (response: GoogleCredentialResponse) => {
          flowActiveRef.current = true;
          loginWithGoogle(response.credential)
            .then(completeAuth)
            .catch((err: Error) => {
              flowActiveRef.current = false;
              setError(formatError(err));
            });
        },
      });

      const hostWidth = googleHostRef.current.offsetWidth;
      google.accounts.id.renderButton(googleHostRef.current, {
        theme: 'outline',
        size: 'large',
        shape: 'rectangular',
        text: 'continue_with',
        width: hostWidth > 0 ? hostWidth : 320,
      });
      if (!cancelled) {
        setGoogleReady(true);
      }
    };

    initGoogle().catch(() => {
      // Google unavailable: email/password still works.
    });

    return () => {
      cancelled = true;
      setGoogleReady(false);
      if (googleHostRef.current) {
        googleHostRef.current.innerHTML = '';
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoginOpen, step, loginWithGoogle]);

  const clearFieldError = (field: CredentialsField) => {
    setFieldErrors((current) => {
      if (!current[field]) {
        return current;
      }
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const setFieldError = (field: CredentialsField, message: string | null) => {
    setFieldErrors((current) => {
      const next = { ...current };
      if (message) {
        next[field] = message;
      } else {
        delete next[field];
      }
      return next;
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting) {
      return;
    }

    const validationErrors = validateCredentials(mode, { name, email, password });
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      setError(null);
      return;
    }

    setFieldErrors({});
    setIsSubmitting(true);
    setError(null);
    flowActiveRef.current = true;
    try {
      if (mode === 'login') {
        const result = await login(email.trim(), password);
        if (result.mfaRequired) {
          setMfaToken(result.mfaToken);
          setStep('challenge');
          setIsSubmitting(false);
          return;
        }
        setIsSubmitting(false);
        completeAuth(result.user);
      } else {
        const created = await register(email.trim(), name, password);
        setIsSubmitting(false);
        completeAuth(created);
      }
    } catch (err) {
      flowActiveRef.current = false;
      setError(formatError(err));
      setIsSubmitting(false);
    }
  };

  const handleMfaSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting || !mfaToken) {
      return;
    }

    const codeError = validateMfaCode(mfaCode);
    if (codeError) {
      setMfaFieldError(codeError);
      setError(null);
      return;
    }

    setMfaFieldError(null);
    setIsSubmitting(true);
    setError(null);
    try {
      await verifyMfaLogin(mfaToken, mfaCode.trim());
      finishAuth();
    } catch (err) {
      setError(formatError(err));
      setIsSubmitting(false);
    }
  };

  const switchMode = () => {
    setMode((current) => (current === 'login' ? 'register' : 'login'));
    setError(null);
    setFieldErrors({});
  };

  const handleForgotSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting) {
      return;
    }

    const emailError = validateEmail(email);
    if (emailError) {
      setForgotEmailError(emailError);
      setError(null);
      return;
    }

    setForgotEmailError(null);
    setIsSubmitting(true);
    setError(null);

    try {
      await requestPasswordReset({ email: email.trim() });
      setStep('forgot-sent');
    } catch (err) {
      setError(formatError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const headerCopyByStep: Record<DrawerStep, { title: string; subtitle: string }> = {
    credentials:
      mode === 'login'
        ? { title: t('auth.welcomeBack'), subtitle: t('auth.welcomeBackSubtitle') }
        : { title: t('auth.createAccount'), subtitle: t('auth.createAccountSubtitle') },
    challenge: {
      title: t('auth.twoStepVerification'),
      subtitle: t('auth.twoStepVerificationSubtitle'),
    },
    onboarding: {
      title: t('auth.secureAccount'),
      subtitle: t('auth.secureAccountSubtitle'),
    },
    forgot: {
      title: t('auth.forgotPassword'),
      subtitle: t('auth.forgotPasswordSubtitle'),
    },
    'forgot-sent': {
      title: t('auth.forgotPassword'),
      subtitle: t('auth.resetEmailSent'),
    },
  };
  const headerCopy = headerCopyByStep[step];

  return (
    <>
      <Overlay $open={isLoginOpen} onClick={closeLogin} aria-hidden={!isLoginOpen} />
      <Drawer
        ref={drawerRef}
        $open={isLoginOpen}
        role="dialog"
        aria-modal="true"
        aria-labelledby="login-drawer-title"
        aria-hidden={!isLoginOpen}
        onClick={(event) => event.stopPropagation()}
      >
        <DrawerBrandRow>
          <BrandLockup logoHeight={22} />
          <CloseButton type="button" onClick={closeLogin} aria-label={t('auth.closeSignIn')}>
            ×
          </CloseButton>
        </DrawerBrandRow>

        <Intro>
          <Title id="login-drawer-title">{headerCopy.title}</Title>
          <Subtitle>{headerCopy.subtitle}</Subtitle>
        </Intro>

        {step === 'challenge' && (
          <Form noValidate onSubmit={handleMfaSubmit}>
            <AuthField>
              <AuthFieldLabel htmlFor="login-mfa-code">{t('auth.authCode')}</AuthFieldLabel>
              <AuthInputShell $invalid={Boolean(mfaFieldError)}>
                <AuthCodeInput
                  id="login-mfa-code"
                  value={mfaCode}
                  onChange={(event) => {
                    setMfaCode(event.target.value.replace(/\D/g, '').slice(0, 6));
                    if (mfaFieldError) {
                      setMfaFieldError(null);
                    }
                  }}
                  onBlur={() => setMfaFieldError(validateMfaCode(mfaCode))}
                  placeholder="000000"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  maxLength={6}
                  $invalid={Boolean(mfaFieldError)}
                  aria-invalid={Boolean(mfaFieldError)}
                  aria-describedby={mfaFieldError ? 'login-mfa-code-error' : undefined}
                />
              </AuthInputShell>
              {mfaFieldError && (
                <FieldError id="login-mfa-code-error" role="alert">
                  {t(mfaFieldError)}
                </FieldError>
              )}
            </AuthField>
            {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
            <FullWidthButton type="submit" $variant="primary" $size="lg" disabled={isSubmitting}>
              {isSubmitting ? t('auth.verifying') : t('auth.verifyAndSignIn')}
            </FullWidthButton>
          </Form>
        )}

        {step === 'onboarding' && (
          <MfaEnrollment
            onEnabled={(updated) => {
              applyUser(updated);
              finishAuth();
            }}
            onSkip={(updated) => {
              applyUser(updated);
              finishAuth();
            }}
          />
        )}

        {step === 'forgot' && (
          <Form noValidate onSubmit={handleForgotSubmit}>
            <AuthField>
              <AuthFieldLabel htmlFor="forgot-email">{t('auth.email')}</AuthFieldLabel>
              <AuthInputShell $invalid={Boolean(forgotEmailError)}>
                <AuthInputIcon aria-hidden="true">@</AuthInputIcon>
                <AuthTextInput
                  id="forgot-email"
                  type="email"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    if (forgotEmailError) {
                      setForgotEmailError(null);
                    }
                  }}
                  onBlur={() => setForgotEmailError(validateEmail(email))}
                  placeholder={t('auth.emailPlaceholder')}
                  autoComplete="email"
                  autoFocus
                  $invalid={Boolean(forgotEmailError)}
                  aria-invalid={Boolean(forgotEmailError)}
                  aria-describedby={forgotEmailError ? 'forgot-email-error' : undefined}
                />
              </AuthInputShell>
              {forgotEmailError && (
                <FieldError id="forgot-email-error" role="alert">
                  {t(forgotEmailError)}
                </FieldError>
              )}
            </AuthField>
            {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
            <FullWidthButton type="submit" $variant="primary" $size="lg" disabled={isSubmitting}>
              {isSubmitting ? t('auth.pleaseWait') : t('auth.sendResetLink')}
            </FullWidthButton>
            <SwitchModeText>
              <SwitchModeButton type="button" onClick={() => setStep('credentials')}>
                {t('auth.backToSignIn')}
              </SwitchModeButton>
            </SwitchModeText>
          </Form>
        )}

        {step === 'forgot-sent' && (
          <FullWidthButton
            type="button"
            $variant="primary"
            $size="lg"
            onClick={() => setStep('credentials')}
          >
            {t('auth.backToSignIn')}
          </FullWidthButton>
        )}

        {step === 'credentials' && (
          <Form noValidate onSubmit={handleSubmit}>
            {mode === 'register' && (
              <AuthField>
                <AuthFieldLabel htmlFor="login-name">{t('auth.name')}</AuthFieldLabel>
                <AuthInputShell $invalid={Boolean(fieldErrors.name)}>
                  <AuthTextInput
                    id="login-name"
                    value={name}
                    onChange={(event) => {
                      setName(event.target.value);
                      clearFieldError('name');
                    }}
                    onBlur={() => setFieldError('name', validateName(name))}
                    placeholder={t('auth.namePlaceholder')}
                    autoComplete="name"
                    $invalid={Boolean(fieldErrors.name)}
                    aria-invalid={Boolean(fieldErrors.name)}
                    aria-describedby={fieldErrors.name ? 'login-name-error' : undefined}
                  />
                </AuthInputShell>
                {fieldErrors.name && (
                  <FieldError id="login-name-error" role="alert">
                    {t(fieldErrors.name)}
                  </FieldError>
                )}
              </AuthField>
            )}
            <AuthField>
              <AuthFieldLabel htmlFor="login-email">{t('auth.email')}</AuthFieldLabel>
              <AuthInputShell $invalid={Boolean(fieldErrors.email)}>
                <AuthInputIcon aria-hidden="true">@</AuthInputIcon>
                <AuthTextInput
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    clearFieldError('email');
                  }}
                  onBlur={() => setFieldError('email', validateEmail(email))}
                  placeholder={t('auth.emailPlaceholder')}
                  autoComplete="email"
                  $invalid={Boolean(fieldErrors.email)}
                  aria-invalid={Boolean(fieldErrors.email)}
                  aria-describedby={fieldErrors.email ? 'login-email-error' : undefined}
                />
              </AuthInputShell>
              {fieldErrors.email && (
                <FieldError id="login-email-error" role="alert">
                  {t(fieldErrors.email)}
                </FieldError>
              )}
            </AuthField>
            <AuthField>
              <AuthFieldLabel htmlFor="login-password">{t('auth.password')}</AuthFieldLabel>
              <PasswordInput
                id="login-password"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  clearFieldError('password');
                }}
                onBlur={() => {
                  const passwordError =
                    mode === 'register'
                      ? validateRegisterPassword(password)
                      : validateLoginPassword(password);
                  setFieldError('password', passwordError);
                }}
                placeholder={
                  mode === 'register'
                    ? t('auth.passwordPlaceholderRegister')
                    : t('auth.passwordPlaceholderLogin')
                }
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                $invalid={Boolean(fieldErrors.password)}
                aria-invalid={Boolean(fieldErrors.password)}
                aria-describedby={fieldErrors.password ? 'login-password-error' : undefined}
              />
              {fieldErrors.password && (
                <FieldError id="login-password-error" role="alert">
                  {t(fieldErrors.password)}
                </FieldError>
              )}
              {mode === 'login' && (
                <ForgotPasswordRow>
                  <ForgotPasswordLink type="button" onClick={() => setStep('forgot')}>
                    {t('auth.forgotPasswordLink')}
                  </ForgotPasswordLink>
                </ForgotPasswordRow>
              )}
            </AuthField>
            {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
            <FullWidthButton type="submit" $variant="primary" $size="lg" disabled={isSubmitting}>
              {isSubmitting
                ? t('auth.pleaseWait')
                : mode === 'login'
                  ? t('auth.signIn')
                  : t('auth.createAccountButton')}
            </FullWidthButton>
          </Form>
        )}

        {step === 'credentials' && (
          <>
            {googleReady && <Divider>{t('common.or')}</Divider>}
            <GoogleButtonHost ref={googleHostRef} $visible={googleReady} />

            <SwitchModeText>
              {mode === 'login' ? t('auth.noAccount') + ' ' : t('auth.hasAccount') + ' '}
              <SwitchModeButton type="button" onClick={switchMode}>
                {mode === 'login' ? t('auth.register') : t('auth.signIn')}
              </SwitchModeButton>
            </SwitchModeText>
          </>
        )}
      </Drawer>
    </>
  );
};

export default LoginDrawer;
